import { literals } from "@/constants/literals";
import { useState } from "react";

import PrimaryButton from "@/components/buttons/primary-button/PrimaryButton";
import { useNavigate } from "react-router-dom";
import type { CapturedPhoto, GroupData, ExitsStep } from "@/types/types.d";
import { CameraCapture } from "../../components/CameraCapture";
import Modal from "@/components/modal/Modal";
import SecondaryButton from "@/components/buttons/secondary-button/SecondaryButton";
import { BASE_URL } from "@/constants/constants";
import useExitsDataStore from "@/store/useExitsData.store";
import ExitsOrderHeader from "../components/ExitsOrderHeader";
import { useDevice } from "@/store/useDevice";
import ExitsGroupMobileInputs from "../components/ExitsGroupMobileInputs";
import ExitsGroupTabletInputs from "../components/ExitsGroupTabletInputs";

const initial_status = {
  description: "",
  extraInformation: "",
  visibleForClient: true,
  photos: [],
  id: "",
};

const assignGroupId = () => {
  const now = new Date();
  return now.getTime();
};

const ExitsNewGroupPage = () => {
  const [groupData, setGroupData] = useState<GroupData>(initial_status);
  const [viewModal, setViewModal] = useState(false);
  const [groupId, setGroupId] = useState(assignGroupId());
  const [description, setDescription] = useState("");
  const [extraInformation, setExtraInformation] = useState("");

  const { addExitsGroupData, exitsData } = useExitsDataStore();
  const navigate = useNavigate();
  const { isMobile } = useDevice();

  const handleOnChange = (
    id: string,
    value: string | boolean | CapturedPhoto[],
  ) => {
    setGroupData((prevState) => {
      const tmp: any = { ...prevState };
      tmp[id] = value;
      return tmp;
    });
  };

  const isValid = () => {
    if (exitsData.step === literals.packagingAction) {
      return groupData.photos.length > 0 && description.trim() !== "";
    }
    return groupData.photos.length > 0;
  };

  const changeDescription = (value: string) => {
    setDescription(value);
    if (value === "") {
      handleOnChange("description", "");
      return;
    }
    const entireDescription =
      extraInformation.trim() !== "" ? `${value}: ${extraInformation}` : value;
    handleOnChange("description", entireDescription);
  };

  const changeExtraInformation = (value: string) => {
    setExtraInformation(value);
    if (value.trim() === "") {
      handleOnChange("description", description);
    } else {
      handleOnChange("description", `${description}: ${value}`);
    }
  };

  const handleOnSubmit = (
    e: React.BaseSyntheticEvent<
      Event,
      EventTarget & HTMLFormElement,
      EventTarget
    >,
  ) => {
    e.preventDefault();
    if (isValid()) {
      setViewModal(true);
    }
  };

  const goToNextCapturePhase = () => {
    if (!isValid()) return;
    addExitsGroupData({ ...groupData, id: groupId });
    navigate(`${BASE_URL}/capture/exits/resume`);
  };

  const cleanPhotosData = () => {
    if (!isValid()) return;
    addExitsGroupData({ ...groupData, id: groupId });
    setGroupId(assignGroupId());
    setGroupData(initial_status);
    setDescription("");
    setExtraInformation("");
    setViewModal(false);
  };

  return (
    <div className="min-h-fulldpx flex flex-col justify-between bg-ib-pt-color-white">
      <ExitsOrderHeader />
      <form
        onSubmit={handleOnSubmit}
        className="flex flex-col flex-1 justify-between"
      >
        <div className="w-full px-4 md:px-14 pt-4 md:flex-1 md:flex md:flex-col md:justify-between">
          <div className="w-full flex-1">
            <CameraCapture
              onChange={(photos) => handleOnChange("photos", photos)}
              maxPhotos={10}
              photos={groupData?.photos || undefined}
            />
          </div>
          {isMobile ? (
            <ExitsGroupMobileInputs
              step={exitsData.step as ExitsStep}
              groupData={groupData}
              description={description}
              extraInformation={extraInformation}
              changeGroupData={handleOnChange}
              changeDescription={changeDescription}
              changeExtraInformation={changeExtraInformation}
            />
          ) : (
            <ExitsGroupTabletInputs
              step={exitsData.step as ExitsStep}
              groupData={groupData}
              description={description}
              extraInformation={extraInformation}
              changeGroupData={handleOnChange}
              changeDescription={changeDescription}
              changeExtraInformation={changeExtraInformation}
            />
          )}
        </div>
        <div className="py-4 flex flex-col pt-4 border-t border-t-ib-pt-color-gray-300 md:flex md:flex-row md:justify-center">
          <div className="px-4 md:capture-button">
            <PrimaryButton
              size="medium"
              label={literals.continue}
              colour="red"
              disabled={!isValid()}
              type="submit"
              onClick={() => {}}
            />
          </div>
        </div>
      </form>
      <Modal
        title={literals.continue}
        opened={viewModal}
        onClose={() => setViewModal(false)}
      >
        <div className="w-full flex flex-col gap-y-4">
          <SecondaryButton
            colour="red"
            label={literals.uploadMorePhotos}
            onClick={cleanPhotosData}
          />
          <PrimaryButton
            colour="red"
            icon="checkUnderline"
            label={literals.seeAllPhotos}
            onClick={goToNextCapturePhase}
          />
        </div>
      </Modal>
    </div>
  );
};

export default ExitsNewGroupPage;
